import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";
import { TEAM } from "../../data/content";

/**
 * AuthorBioCard — renders a compact author profile card at the end of blog
 * posts. Matches author name against TEAM entries (must have `href`) and
 * gracefully returns null when unmatched.
 */
export default function AuthorBioCard({ authorName }) {
  if (!authorName) return null;
  const key = authorName.toLowerCase();
  const author = TEAM.find((m) => {
    if (!m.href) return false;
    const surname = m.name.toLowerCase().split(/\s+/).slice(-1)[0]; // "sharma" / "patait"
    return key.includes(surname);
  });
  if (!author) return null;

  const anchorSlug =
    author.slug === "manishika-patait" ? "maternal-enquiry" : "amit-enquiry";
  const shortName = author.name.split(" ").slice(0, 2).join(" "); // "Dr. Amit"

  return (
    <aside
      className="mt-14 card-soft p-6 md:p-7 flex flex-col md:flex-row gap-6 items-start md:items-center border border-brand-cream"
      data-testid={`author-bio-${author.slug}`}
    >
      <Link
        to={author.href}
        className="shrink-0 block"
        data-testid={`author-bio-photo-${author.slug}`}
        aria-label={`Visit ${author.name}'s profile`}
      >
        <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden bg-brand-cream">
          <img
            src={author.photo}
            alt={author.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </Link>

      <div className="flex-1 min-w-0">
        <div className="eyebrow mb-1 flex items-center gap-2">
          <Award size={12} /> About the author
        </div>
        <Link
          to={author.href}
          className="font-heading text-xl text-brand-ink hover:text-brand-olive block leading-tight"
          data-testid={`author-bio-name-${author.slug}`}
        >
          {author.name}
        </Link>
        <div className="text-sm text-brand-terracotta mt-0.5">{author.title}</div>
        <p className="text-sm text-brand-ink2 mt-2 line-clamp-3">{author.bio}</p>

        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to={author.href}
            className="btn-outline text-sm !py-2 !px-4"
            data-testid={`author-bio-profile-${author.slug}`}
          >
            View Profile <ArrowRight size={14} />
          </Link>
          <Link
            to={`${author.href}#${anchorSlug}`}
            className="btn-primary text-sm !py-2 !px-4"
            data-testid={`author-bio-enquire-${author.slug}`}
          >
            Enquire with {shortName}
          </Link>
        </div>
      </div>
    </aside>
  );
}
